import { type Dispatch, type SetStateAction, useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { deleteCoupon, type Coupon } from './apiHelpers';

export interface DeleteCouponConfirmationModalProps {
  closeModal: () => void;
  coupon: Coupon;
  isOpen: boolean;
  onDeleted: () => void;
  restaurantId: string;
  setCouponToDelete: Dispatch<SetStateAction<Coupon | undefined>>;
}

export const DeleteCouponConfirmationModal = ({
  closeModal,
  coupon,
  isOpen,
  onDeleted,
  restaurantId,
  setCouponToDelete,
}: DeleteCouponConfirmationModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteCoupon = async (): Promise<void> => {
    setIsDeleting(true);
    try {
      await deleteCoupon(restaurantId, coupon.id);
      setCouponToDelete(undefined);
      closeModal();
      onDeleted();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({ message: 'An error occurred' });
      }
      reportAppError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = (): void => {
    setCouponToDelete(undefined);
    closeModal();
  };

  return (
    <Modal
      ariaLabel="confirm delete coupon modal"
      isOpen={isOpen}
      onClose={handleClose}
      subtitle={`Are you sure you would like to delete the coupon “${coupon.name}”?`}
    >
      <ModalActions>
        <Button
          isDisabled={isDeleting}
          label="Cancel"
          onClick={handleClose}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          isDisabled={isDeleting}
          isLoading={isDeleting}
          label="Yes, delete coupon"
          onClick={() => {
            void handleDeleteCoupon();
          }}
          variant={ButtonVariants.Primary}
        />
      </ModalActions>
    </Modal>
  );
};
